// The page half of the Web Share Target (public/share-target-sw.js is the other half).
//
// The service worker stored the shared recording in a cache and redirected here; this reads it
// back out and hands it to the upload queue, so the flow the owner sees is: tap Share in Samsung
// Voice Recorder → LifeOS opens with the recording already saved on the phone → choose a course.
//
// THE CACHE IS A HANDOVER, NOT A STORE. A staged share is the only copy LifeOS holds until the
// queue's IndexedDB transaction commits, which is why nothing here deletes on read (see
// `listStagedShares` below) and why the worker stages each share under its own key.

/** These constants are duplicated in public/share-target-sw.js; a test asserts they still match. */
export const SHARED_AUDIO_CACHE = 'lifeos-shared-audio'
/**
 * Every staged share lives at `<prefix><stageId>`, one key each.
 *
 * The service worker used to write one fixed key, so sharing two lectures before opening the app
 * lost the first. Listing the prefix is what makes "two shares, both still here" true.
 */
export const SHARED_AUDIO_PREFIX = '/__lifeos-shared-audio__/'
/**
 * The key the PREVIOUS service worker wrote, kept because a phone can be carrying one.
 *
 * It sits under the prefix above, so the listing below finds it without a special case; this
 * constant exists so that a test can name it and so the reason is written down somewhere.
 */
export const SHARED_AUDIO_KEY = '/__lifeos-shared-audio__/pending'
/** Where a share with no file — a link, a selection — waits for the capture sheet. */
export const SHARED_TEXT_KEY = '/__lifeos-shared-text__/pending'

/** One share, as the service worker left it. The key IS the stage id, and it is the queue's. */
export interface StagedShare {
  /** The part of the cache key after the prefix. Unique per share, minted by the worker. */
  stageId: string
  /** The full cache key, so a caller can delete exactly what it consumed. */
  key: string
  file: File
}

/** What the `?shared=` parameter on /record can say, straight from the service worker. */
export type SharedAudioOutcome = '1' | 'empty' | 'failed'

/** The stage id out of a cache key, whether the key arrived as a string or as a Request. */
function stageIdOf(key: string): string | null {
  let pathname = key
  try {
    pathname = new URL(key, 'https://lifeos.invalid').pathname
  } catch {
    // A key that is not URL-shaped is used as-is. It came out of our own cache either way.
  }
  if (!pathname.startsWith(SHARED_AUDIO_PREFIX)) return null
  const id = pathname.slice(SHARED_AUDIO_PREFIX.length)
  return id.length > 0 ? id : null
}

/**
 * READ, not take — and that is the change this whole feature turns on.
 *
 * The old version deleted the entry as it handed it over, which was correct while the file went
 * straight into an upload the owner was watching. It is wrong now: the durable queue writes the
 * recording into IndexedDB first, and the staged copy is the ONLY copy until that write commits.
 * Deleting before the commit is how a share is lost to a browser that refuses storage. So this
 * lists, the queue writes, and `dropStagedShare` deletes — in that order, never any other
 * (lib/upload-queue.ts → importStagedShares).
 *
 * Returns an empty list for every reason a share can be absent (no Cache API, nothing stored, a
 * private window that refuses storage). None of them is an error worth throwing at a screen whose
 * job is to let someone pick a file: the picker is right there.
 */
export async function listStagedShares(): Promise<StagedShare[]> {
  if (typeof caches === 'undefined') return []
  try {
    const cache = await caches.open(SHARED_AUDIO_CACHE)
    const keys = await cache.keys()
    const shares: StagedShare[] = []
    for (const request of keys) {
      const key = typeof request === 'string' ? request : request.url
      const stageId = stageIdOf(key)
      if (!stageId) continue

      const stored = await cache.match(request)
      if (!stored) continue
      const blob = await stored.blob()
      // A zero-byte stage is not a recording. It is dropped rather than queued, because a queue
      // entry that can never upload anything is a permanent red row about nothing.
      if (blob.size === 0) {
        await cache.delete(request).catch(() => false)
        continue
      }

      const encoded = stored.headers.get('x-lifeos-filename')
      let name = 'shared-recording'
      if (encoded) {
        try {
          name = decodeURIComponent(encoded) || name
        } catch {
          // A filename that will not decode is not a reason to lose the audio. The bytes matter;
          // the name is provenance, and a fallback name is visibly a fallback.
        }
      }

      const type = stored.headers.get('content-type') || blob.type || 'application/octet-stream'
      shares.push({ stageId, key, file: new File([blob], name, { type }) })
    }
    return shares
  } catch {
    return []
  }
}

/**
 * Delete one staged share, by the exact key it was listed under.
 *
 * Called ONLY after the queue's own transaction has committed. Returns false when nothing was
 * deleted — which is not an error and is not worth a screen: the entry is already durable, and a
 * stage that outlives it is re-imported onto the same entry by its stage id rather than becoming
 * a second copy (lib/upload-queue.ts).
 */
export async function dropStagedShare(key: string): Promise<boolean> {
  if (typeof caches === 'undefined') return false
  try {
    const cache = await caches.open(SHARED_AUDIO_CACHE)
    return await cache.delete(key)
  } catch {
    return false
  }
}

/**
 * The same take-once contract for a share that carried WORDS rather than a file —
 * a link from a browser, a selection from a reader — which the mobile capture
 * sheet opens pre-filled with.
 *
 * TAKE, not read, for the reason above it: a share is consumed once, and leaving
 * it in the cache would mean the next cold start silently re-offers a link the
 * owner already saved.
 */
export async function takeSharedText(): Promise<string | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open(SHARED_AUDIO_CACHE)
    const stored = await cache.match(SHARED_TEXT_KEY)
    if (!stored) return null
    await cache.delete(SHARED_TEXT_KEY)
    const text = (await stored.text()).trim()
    return text.length > 0 ? text : null
  } catch {
    return null
  }
}
